/* eslint-disable array-callback-return */
/* eslint-disable max-len */
import { FriendsInfoStatus, QryActInfoRes, SharePrizeInfo, SharePrizeInfoStatus } from '@tencent/wphk-act-base-cgi';
import { QryAndFormatCouponItem } from '@tencent/wphk-act-base-types';
import { amount, to } from '@tencent/wphk-base-utils';
import { showBusinessError } from '@tencent/wphk-components-dialog';
import { showToast, ToastType } from '@tencent/wphk-components-toast';
import { computed, reactive, ref } from 'vue-demi';
import {
  exchangeCoupon as exchangeCouponApi,
  formatNickName,
  getC2CActInfo,
  getC2CCouponList,
  getC2CDialogCouponState,
  getC2CIndexCouponState,
} from '../business';
import { COUPON_DIALOG_BTN_CONF } from '../config';
import { i18n } from '../i18n';
import { C2CCoupon, C2CIndexCouponState, C2CUserType, ShowLogoType, ShowTextColor, ShowType } from '../types';
import { tryDecode } from '../utils';

/** c2c活动信息 */
const c2cActInfo = reactive<{ [key in C2CUserType]: QryActInfoRes | null }>({
  [C2CUserType.isSelf]: null,
  [C2CUserType.notSelf]: null,
});
/** c2c券信息 */
const c2cCouponList = ref<C2CCoupon[]>([]);
/** 当前链接分享码是否本人的 */
const isSelfUrlShareCode = ref(false);

export const useActInfo = () => {
  /** 初始化分享活动信息 */
  async function initActInfo(actId: string, shareCode = '') {
    let params = { actId };
    if (shareCode) {
      params = Object.assign(params, { shareCode: tryDecode(shareCode) });
    }
    const actInfo = await getC2CActInfo(params);
    // 码是本人的
    if (actInfo.isSameUser || !shareCode) {
      c2cActInfo[C2CUserType.isSelf] = actInfo;
      isSelfUrlShareCode.value = true;
    } else {
      // 码是非本人的
      c2cActInfo[C2CUserType.notSelf] = actInfo;
      c2cActInfo[C2CUserType.isSelf] = await getC2CActInfo({ actId });
      isSelfUrlShareCode.value = false;
    }
    c2cCouponList.value = await qryCouponList();
  }

  /** 阶段二好友信息 */
  const phase2FriendsInfo = computed(() => c2cActInfo[C2CUserType.isSelf]?.phase2FriendsInfo || []);

  /** 最大邀请人数 */
  const maxInviteCount = computed(() => c2cActInfo[C2CUserType.isSelf]?.help2Times || 0);

  /** 成功邀请人数 */
  const successInviteCount = computed(() => phase2FriendsInfo.value.filter(ele => ele.state === FriendsInfoStatus.getAward).length);

  /** 头像昵称信息：如果之前绑定过，则都为绑定人的信息；如果之前没有绑定过，则为当前分享码的人的信息 */
  const shareCodeUserInfo = computed(() => {
    if (!c2cActInfo[C2CUserType.notSelf]) return null;
    const { hasHelp, shareCodeNickName } = c2cActInfo[C2CUserType.notSelf];
    return {
      hasHelp,
      nickName: formatNickName(shareCodeNickName),
    };
  });

  /** 分享人奖励总金额 */
  const allCouponDiscount = computed(() => {
    const sum = c2cCouponList.value.reduce((acc, ele) => acc + (ele.couponInfo.rawDiscount || 0), 0);
    return amount.fen2Yuan(String(sum));
  });

  /** 初始化券信息 */
  async function qryCouponList() {
    const prizeList = c2cActInfo[C2CUserType.isSelf]?.phase2SharePrizeList || [];
    if (!prizeList.length) return [];
    const couponList = await getC2CCouponList(prizeList);
    const couponShowList = prizeList.sort((a, b) => a.threshold - b.threshold);
    return formatC2CCouponList(couponShowList, couponList);
  }

  /** 已全部兑换 */
  const isAllExchanged = computed(() => c2cCouponList.value.every(ele => ele.prizeInfo.status === SharePrizeInfoStatus.Exchanged));

  function getShowText(index: number) {
    if (index === 1) {
      return i18n('第1位', { index });
    }
    if (index === 2) {
      return i18n('第2位', { index });
    }
    if (index === 3) {
      return i18n('第3位', { index });
    }
    return i18n('第X位', { index });
  }

  function getShowInfoByStatus(params: {
    indexStatus: C2CIndexCouponState;
    firstInviteIndex: number;
    currentIdx: number;
  }) {
    const { indexStatus, firstInviteIndex, currentIdx } = params;
    // 已兑换
    if (indexStatus === C2CIndexCouponState.exchanged) {
      return {
        logoType: ShowLogoType.png,
        type: ShowType.icon,
      };
    }
    // 可兑换
    if (indexStatus === C2CIndexCouponState.exchangeable) {
      return {
        logoType: ShowLogoType.gif,
        type: ShowType.btn,
        text: i18n('領取'),
      };
    }
    // 待邀请
    if (firstInviteIndex === currentIdx) {
      return {
        logoType: firstInviteIndex === 0 ? ShowLogoType.gif : ShowLogoType.png,
        type: ShowType.text,
        text: firstInviteIndex === 0 ? i18n('邀請1人可拎') : i18n('再邀請1人可拎'),
        color: ShowTextColor.green,
      };
    }
    // 待邀请
    return {
      logoType: ShowLogoType.png,
      type: ShowType.text,
      text: getShowText(currentIdx + 1),
      color: ShowTextColor.grey,
    };
  }

  /** 格式化券信息 */
  function formatC2CCouponList(couponShowList: SharePrizeInfo[], couponList: QryAndFormatCouponItem[]): C2CCoupon[] {
    // 第一个可邀请的索引
    const firstInviteIndex = couponShowList.findIndex(ele => [SharePrizeInfoStatus.UnExchangeableNoQual, SharePrizeInfoStatus.UnExchangeableInvalidType].includes(ele.status));
    /** 已过期 已领取 总库存无 今日库存无 未领取 */
    return couponShowList
      .map((ele, index) => {
        const coupon = couponList.find(coupon => coupon.qualId === ele.prizeId);
        if (coupon) {
          // 首页券状态
          const indexStatus = getC2CIndexCouponState(ele.status);
          // 弹窗券状态
          const dialogStatus = getC2CDialogCouponState(coupon, indexStatus);
          // 首个可邀请券索引
          const isfirstInvite = firstInviteIndex === index;
          return {
            indexStatus,
            dialogStatus,
            prizeInfo: ele,
            couponInfo: coupon,
            btnInfo: COUPON_DIALOG_BTN_CONF[dialogStatus],
            showInfo: getShowInfoByStatus({
              indexStatus,
              firstInviteIndex,
              currentIdx: index,
            }),
            isfirstInvite,
          };
        }
      })
      .filter(ele => !!ele) as C2CCoupon[];
  }

  /** 资格兑换奖品 */
  async function exchangeCoupon(actId: string, qualId: string, actListid: string, prizeNo: string) {
    const [exchErr, isSuccess] = await to(exchangeCouponApi(qualId, actListid, prizeNo));
    // 刷新数据
    await initActInfo(actId);
    if (exchErr) {
      showBusinessError(exchErr);
      return;
    }
    if (!isSuccess) {
      showBusinessError('抱歉，今日優惠已領完，請明日再來。');
      return;
    }
    showToast({
      type: ToastType.Tick,
      title: i18n('領取成功'),
    });
  }

  return {
    initActInfo,
    c2cCouponList,
    exchangeCoupon,
    isSelfUrlShareCode,
    successInviteCount,
    shareCodeUserInfo,
    phase2FriendsInfo,
    maxInviteCount,
    allCouponDiscount,
    isAllExchanged,
  };
};
